import React, { useState } from 'react';
import { useAuth } from '../App';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Eye, EyeOff, User, Lock, Mail, UserPlus, Phone } from 'lucide-react';

const LoginForm = () => {
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Registration form state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'client'
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const success = await login(loginData.email, loginData.password);
      if (!success) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const success = await register(registerData);
      if (!success) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <Card className="w-full max-w-md mx-auto shadow-xl">
        <CardHeader className="text-center space-y-2 pb-6">
          <CardTitle className="text-2xl sm:text-3xl font-bold text-teal-700">Tribù in Viaggio</CardTitle>
          <CardDescription className="text-sm sm:text-base text-slate-600">
            Sistema di gestione viaggi per Tribù in Viaggio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" className="flex items-center gap-2 text-sm sm:text-base py-2 sm:py-3">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Accedi</span>
                <span className="sm:hidden">Login</span>
              </TabsTrigger>
              <TabsTrigger value="register" className="flex items-center gap-2 text-sm sm:text-base py-2 sm:py-3">
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Registrati</span>
                <span className="sm:hidden">Sign Up</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2 text-sm sm:text-base">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    required
                    className="transition-all duration-200 text-sm sm:text-base py-2 sm:py-3"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="flex items-center gap-2 text-sm sm:text-base">
                    <Lock className="w-4 h-4" />
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      required
                      className="pr-10 transition-all duration-200 text-sm sm:text-base py-2 sm:py-3"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white text-sm sm:text-base py-2 sm:py-3 transition-all duration-200" 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span className="hidden sm:inline">Accesso in corso...</span>
                      <span className="sm:hidden">Loading...</span>
                    </div>
                  ) : (
                    'Accedi'
                  )}
                </Button>
              </form>
            </TabsContent>

          <TabsContent value="register" className="mt-6">
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-sm sm:text-base">Nome</Label>
                  <Input
                    id="first_name"
                    type="text"
                    placeholder="Mario"
                    value={registerData.first_name}
                    onChange={(e) => setRegisterData({...registerData, first_name: e.target.value})}
                    required
                    className="text-sm sm:text-base py-2 sm:py-3"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-sm sm:text-base">Cognome</Label>
                  <Input
                    id="last_name"
                    type="text"
                    placeholder="Rossi"
                    value={registerData.last_name}
                    onChange={(e) => setRegisterData({...registerData, last_name: e.target.value})}
                    required
                    className="text-sm sm:text-base py-2 sm:py-3"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg_email" className="flex items-center gap-2 text-sm sm:text-base">
                  <Mail className="w-4 h-4" />
                  Email
                </Label>
                <Input
                  id="reg_email"
                  type="email"
                  placeholder="your@email.com"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                  required
                  className="text-sm sm:text-base py-2 sm:py-3"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2 text-sm sm:text-base">
                  <Phone className="w-4 h-4" />
                  Telefono
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+39 123 456 7890"
                  value={registerData.phone}
                  onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                  className="text-sm sm:text-base py-2 sm:py-3"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg_password" className="flex items-center gap-2 text-sm sm:text-base">
                  <Lock className="w-4 h-4" />
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="reg_password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                    required
                    className="pr-10 text-sm sm:text-base py-2 sm:py-3"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role" className="text-sm sm:text-base">Ruolo</Label>
                <select
                  id="role"
                  value={registerData.role}
                  onChange={(e) => setRegisterData({...registerData, role: e.target.value})}
                  className="w-full px-3 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm sm:text-base"
                >
                  <option value="client">Cliente</option>
                  <option value="agent">Agente</option>
                </select>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700 text-white text-sm sm:text-base py-2 sm:py-3 transition-all duration-200" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    <span className="hidden sm:inline">Registrazione in corso...</span>
                    <span className="sm:hidden">Loading...</span>
                  </div>
                ) : (
                  'Registrati'
                )}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
    </div>
  );
};

export default LoginForm;